import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { MdInfoOutline } from 'react-icons/md';

const CF_GUIDE_STEPS = [
  {
    color: '#3b82f6',
    title: 'Set Your OpenAI API Key',
    desc: 'Paste your OpenAI key (starts with sk-) in the header field and click Save. The key is required for AI generation and conversion. Click ? for help getting one.',
  },
  {
    color: '#10b981',
    title: 'Generate a Function with AI',
    desc: 'Click "Create Function". Provide a name, a plain-English description of what the function should do, and one input→output example. AI writes the Python code for you.',
  },
  {
    color: '#f59e0b',
    title: 'Convert Existing Code',
    desc: 'Have messy JS, Python, or pseudocode? Click "Add Existing Function", paste your code, and AI rewrites it into a clean, compatible Python toolkit function.',
  },
  {
    color: '#8b5cf6',
    title: 'Use in a Test Suite',
    desc: 'In Test Suite Builder, add the function as a suite step and fill in "Store Result As" (e.g. accessToken). The return value is then available as {{accessToken}} in later steps.',
  },
  {
    color: '#ec4899',
    title: 'View & Edit Generated Code',
    desc: 'Click "View/Update Code" on any function card to inspect or manually edit the generated Python. Click Save when done to persist your changes.',
  },
];

function CustomFunctionsQuickStartGuide() {
  const [open, setOpen] = useState(false);
  return (
    <div className="card" style={{ marginBottom: '16px', padding: '14px 18px', border: '1px solid var(--border)' }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}
      >
        <MdInfoOutline color="var(--primary)" size={18} />
        <strong style={{ fontSize: '14px' }}>Custom Functions — Quick Start Guide</strong>
        <span style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--text-muted)' }}>{open ? '▲ Hide' : '▼ Show'}</span>
      </div>
      {open && (
        <div style={{ marginTop: '14px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px' }}>
          {CF_GUIDE_STEPS.map((s, i) => (
            <div key={i} style={{
              display: 'flex', gap: '12px', alignItems: 'flex-start',
              background: 'var(--bg-input)', borderLeft: `4px solid ${s.color}`,
              borderRadius: '8px', padding: '12px',
            }}>
              <div style={{
                minWidth: '24px', height: '24px', borderRadius: '50%',
                backgroundColor: s.color, color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: '12px', flexShrink: 0,
              }}>{i + 1}</div>
              <div>
                <strong style={{ display: 'block', fontSize: '13px', marginBottom: '4px' }}>{s.title}</strong>
                <span style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.5 }}>{s.desc}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CustomFunctions({ openAiApiKey: propOpenAiKey, onApiKeysChange }) {
  const [functions, setFunctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showConvertForm, setShowConvertForm] = useState(false);
  const [showApiKeyHelp, setShowApiKeyHelp] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [openAiApiKey, setOpenAiApiKey] = useState(propOpenAiKey || '');
  const [isEditingKey, setIsEditingKey] = useState(false);
  const [savingKey, setSavingKey] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    example: ''
  });
  const [convertFormData, setConvertFormData] = useState({
    name: '',
    description: '',
    code: ''
  });
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualFormData, setManualFormData] = useState({ name: '', description: '', example: '', code: '' });
  const [modalAlert, setModalAlert] = useState(null); // { title: '', message: '', type: 'error'|'success' }
  const [savingManual, setSavingManual] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [verificationResults, setVerificationResults] = useState({}); // { [id]: { success, result, error, verifying } }
  const [editingId, setEditingId] = useState(null);
  const [editingCode, setEditingCode] = useState('');
  const [savingId, setSavingId] = useState(null);
  
  // Deletion safeguard state
  const [confirmDeleteData, setConfirmDeleteData] = useState(null); // { id, name, suites }
  const [hardDeleteConfirmText, setHardDeleteConfirmText] = useState('');

  const fetchCustomFunctions = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/custom-functions');
      setFunctions(Array.isArray(response.data) ? response.data : []);
    } catch (fetchError) {
      setError('Failed to load custom functions: ' + fetchError.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomFunctions();
  }, []);

  useEffect(() => {
    setOpenAiApiKey(propOpenAiKey || '');
  }, [propOpenAiKey]);

  const handleSaveApiKey = async () => {
    setSavingKey(true);
    try {
      await axios.put('/api/config', { openAiApiKey });
      if (onApiKeysChange) onApiKeysChange({ openAiApiKey });
      setIsEditingKey(false);
    } catch (error) {
      console.error('Error saving API Key:', error);
      setError('Failed to save API key');
    }
    setSavingKey(false);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleConvertChange = (event) => {
    const { name, value } = event.target;
    setConvertFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleGenerate = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    const name = String(formData.name || '').trim();
    const description = String(formData.description || '').trim();
    const example = String(formData.example || '').trim();
    const apiKey = String(openAiApiKey || propOpenAiKey || '').trim();

    if (!name || !description || !example) {
      setError('Function Name, Description, and Example are required.');
      return;
    }

    if (!apiKey) {
      setModalAlert({
        title: 'API Key Required',
        message: 'OpenAI API Key is mandatory. Add it in the page header before generating.',
        type: 'error'
      });
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post('/api/custom-functions/generate', {
        name,
        description,
        example,
        openAiApiKey: String(openAiApiKey || propOpenAiKey || '').trim()
      });

      const generatedFunction = response.data;
      setFunctions((prev) => [generatedFunction, ...prev]);
      setShowCreateForm(false);
      setFormData({
        name: '',
        description: '',
        example: ''
      });

        setSuccess(`Function "${generatedFunction.name}" generated successfully.`);
    } catch (generateError) {
      const message = generateError.response?.data?.error || generateError.message;
      setError('Failed to generate function: ' + message);
    } finally {
      setGenerating(false);
    }
  };

  const handleConvert = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    const name = String(convertFormData.name || '').trim();
    const description = String(convertFormData.description || '').trim();
    const code = String(convertFormData.code || '').trim();
    const apiKey = String(openAiApiKey || propOpenAiKey || '').trim();

    if (!name || !description || !code) {
      setError('Function Name, Purpose, and Existing Code are required.');
      return;
    }

    if (!apiKey) {
      setModalAlert({
        title: 'API Key Required',
        message: 'OpenAI API Key is mandatory. Add it in the page header before converting.',
        type: 'error'
      });
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post('/api/custom-functions/convert', {
        name,
        description,
        code,
        openAiApiKey: String(openAiApiKey || propOpenAiKey || '').trim()
      });

      const convertedFunction = response.data;
      setFunctions((prev) => [convertedFunction, ...prev]);
      setExpandedId(convertedFunction.id);
      setShowConvertForm(false);
      setConvertFormData({
        name: '',
        description: '',
        code: ''
      });

        setSuccess(`Function "${convertedFunction.name}" converted successfully.`);
    } catch (convertError) {
      const message = convertError.response?.data?.error || convertError.message;
      setError('Failed to convert function: ' + message);
    } finally {
      setGenerating(false);
    }
  };

  const handleManual = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    const name = String(manualFormData.name || '').trim();
    const description = String(manualFormData.description || '').trim();
    const example = String(manualFormData.example || '').trim();
    const code = String(manualFormData.code || '').trim();
    if (!name || !description || !code) {
      setError('Function Name, Description, and Code are required.');
      return;
    }
    setSavingManual(true);
    try {
      const response = await axios.post('/api/custom-functions/default', { name, description, example, code });
      setFunctions((prev) => [response.data, ...prev]);
      setShowManualForm(false);
      setManualFormData({ name: '', description: '', example: '', code: '' });
      setSuccess(`Function "${response.data.name}" saved successfully.`);
    } catch (err) {
      setError('Failed to save function: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingManual(false);
    }
  };

  const handleVerifyCode = async ({ code = null, fnId = null, fnName = null } = {}) => {
    setError('');
    
    // If code is not provided in params, fallback to manual form code
    const codeToTest = code !== null ? code : String(manualFormData.code || '').trim();
    const nameToTest = fnName || String(manualFormData.name || '').trim() || 'testFunction';

    if (!codeToTest) {
      setError('Please provide Python code to verify.');
      return;
    }

    if (fnId) {
      setVerificationResults(prev => ({ 
        ...prev, 
        [fnId]: { ...prev[fnId], verifying: true, error: null, result: null } 
      }));
    } else {
      setTestResult(null);
      setVerifying(true);
    }

    try {
      const response = await axios.post('/api/custom-functions/test', {
        code: codeToTest,
        name: nameToTest,
        inputVariables: {}
      });
      
      const resultData = { success: true, result: response.data.result };
      
      if (fnId) {
        setVerificationResults(prev => ({ 
          ...prev, 
          [fnId]: { verifying: false, ...resultData } 
        }));
      } else {
        setTestResult(resultData);
      }
    } catch (err) {
      const errorData = { 
        success: false, 
        error: err.response?.data?.error || err.message 
      };
      
      if (fnId) {
        setVerificationResults(prev => ({ 
          ...prev, 
          [fnId]: { verifying: false, ...errorData } 
        }));
      } else {
        setTestResult(errorData);
      }
    } finally {
      if (!fnId) setVerifying(false);
    }
  };

  const handleDelete = async (id, force = false) => {
    setError('');
    setSuccess('');
    
    // Find the name for the modal
    const fnToDelete = functions.find(f => f.id === id);
    if (!fnToDelete) return;

    setDeletingId(id);

    try {
      const url = force ? `/api/custom-functions/${id}?force=true` : `/api/custom-functions/${id}`;
      await axios.delete(url);
      
      setFunctions((prev) => prev.filter((item) => item.id !== id));
      if (expandedId === id) {
        setExpandedId(null);
      }
      setConfirmDeleteData(null);
      setHardDeleteConfirmText('');
      setSuccess(`Custom function "${fnToDelete.name}" deleted successfully.`);
    } catch (deleteError) {
      const resp = deleteError.response?.data;
      if (resp?.error === 'CUSTOM_FUNCTION_IN_USE') {
        setConfirmDeleteData({ 
          id, 
          name: fnToDelete.name, 
          suites: resp.suites || [] 
        });
      } else {
        const message = resp?.error || deleteError.message;
        setError('Failed to delete custom function: ' + message);
      }
    } finally {
      setDeletingId(null);
    }
  };

  const handleSave = async (id) => {
    setError('');
    setSuccess('');
    setSavingId(id);

    try {
      const response = await axios.put(`/api/custom-functions/${id}`, { code: editingCode });
      const updatedFn = response.data;
      
      setFunctions((prev) => prev.map((f) => (f.id === id ? updatedFn : f)));
      setEditingId(null);
      setExpandedId(null);
      setSuccess(`Function "${updatedFn.name}" updated successfully.`);
    } catch (saveError) {
      const message = saveError.response?.data?.error || saveError.message;
      setError('Failed to update function: ' + message);
    } finally {
      setSavingId(null);
    }
  };

  const toggleExpansion = (fn) => {
    if (expandedId === fn.id) {
      setExpandedId(null);
      setEditingId(null);
    } else {
      setExpandedId(fn.id);
      setEditingId(fn.id);
      setEditingCode(fn.code);
    }
  };

  return (
    <div>
      <div className="custom-functions-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '20px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0 }}>Add Custom Function</h1>
        <div className="custom-functions-key-input">
          <div className="custom-functions-key-label-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <label style={{ margin: 0 }}>OpenAI API Key *</label>
            <button
              type="button"
              className="key-help-btn"
              onClick={() => setShowApiKeyHelp((prev) => !prev)}
              aria-label="How to get API keys"
              title="How to get API keys"
            >
              ?
            </button>
          </div>
          {isEditingKey ? (
            <div style={{ display: 'grid', gap: '10px' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span style={{ fontSize: '11px', minWidth: '80px' }}>OpenAI Key:</span>
                <input
                  type="password"
                  value={openAiApiKey}
                  onChange={(event) => setOpenAiApiKey(event.target.value)}
                  placeholder="sk-..."
                  autoComplete="off"
                  style={{ flex: 1, padding: '8px' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  className="btn btn-success"
                  onClick={handleSaveApiKey}
                  disabled={savingKey}
                  style={{ padding: '8px 16px' }}
                >
                  {savingKey ? 'Saving...' : 'Save Key'}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setOpenAiApiKey(propOpenAiKey || '');
                    setIsEditingKey(false);
                  }}
                  style={{ padding: '8px 16px' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '8px' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="password"
                  value={propOpenAiKey ? '********************************' : ''}
                  disabled
                  placeholder="OpenAI Key (sk-...)"
                  style={{ flex: 1, backgroundColor: 'var(--bg-input)', padding: '8px', cursor: 'not-allowed', fontSize: '12px' }}
                />
                <button
                  className="btn btn-primary"
                  onClick={() => setIsEditingKey(true)}
                  style={{ padding: '8px 16px' }}
                >
                  Edit Key
                </button>
              </div>
            </div>
          )}
          {showApiKeyHelp && (
            <div className="custom-functions-key-help-panel" style={{ display: 'block' }}>
              <p><strong>OpenAI API Key:</strong></p>
              <ol style={{ fontSize: '12px', paddingLeft: '20px' }}>
                <li>Login to <a href="https://platform.openai.com" target="_blank" rel="noreferrer">OpenAI Dashboard</a>.</li>
                <li>Go to <strong>API Keys</strong>.</li>
                <li>Create a new secret key (starts with <code>sk-</code>).</li>
              </ol>
            </div>
          )}
        </div>
      </div>

      <CustomFunctionsQuickStartGuide />

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', flexWrap: 'wrap' }}>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            setShowCreateForm((prev) => !prev);
            setShowManualForm(false);
            setTestResult(null);
          }}
          disabled={generating}
        >
          {showCreateForm ? 'Close' : '✦ Create with AI'}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          style={{ borderColor: '#10b981', color: '#10b981' }}
          onClick={() => {
            setShowManualForm((prev) => !prev);
            setShowCreateForm(false);
            setTestResult(null);
          }}
        >
          {showManualForm ? 'Close' : '✎ Write Manually (No AI)'}
        </button>
      </div>

      {showCreateForm && (
        <div className="card">
          <h2>Create Function with AI</h2>
          <p>
            Provide a function name, what the function should do, and one example.
            AI will generate the function and save it to your list.
          </p>

          <form onSubmit={handleGenerate}>
            <div className="form-group">
              <label>Function Name *</label>
              <input
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., normalizePhoneNumber"
                required
              />
            </div>

            <div className="form-group">
              <label>Description *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe exactly what function you need."
                rows={4}
                required
              />
            </div>

            <div className="form-group">
              <label>Example *</label>
              <textarea
                name="example"
                value={formData.example}
                onChange={handleChange}
                placeholder="Input: '+1 (555) 123-4567' => Output: '15551234567'"
                rows={3}
                required
              />
            </div>

            <button type="submit" className="btn btn-success" disabled={generating}>
              {generating ? 'Generating...' : 'Generate Function'}
            </button>
          </form>
        </div>
      )}

      {showManualForm && (
        <div className="card">
          <h2>✎ Write Function Manually</h2>
          <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '16px' }}>
            No AI or API key needed. Write your own Python function and save it directly.
          </p>
          <form onSubmit={handleManual}>
            <div className="form-group">
              <label>Function Name *</label>
              <input
                type="text"
                name="name"
                value={manualFormData.name}
                onChange={(e) => {
                  setManualFormData((p) => ({ ...p, name: e.target.value }));
                  setTestResult(null);
                }}
                placeholder="e.g., generateIndianMobile"
                required
              />
            </div>
            <div className="form-group">
              <label>Description *</label>
              <input
                type="text"
                value={manualFormData.description}
                onChange={(e) => {
                  setManualFormData((p) => ({ ...p, description: e.target.value }));
                  setTestResult(null);
                }}
                placeholder="What does this function do?"
                required
              />
            </div>
            <div className="form-group">
              <label>Example (Optional)</label>
              <input
                type="text"
                value={manualFormData.example}
                onChange={(e) => {
                  setManualFormData((p) => ({ ...p, example: e.target.value }));
                  setTestResult(null);
                }}
                placeholder="e.g., Output: 919995805992"
              />
                 <div className="form-group">
              <label>Python Code *</label>
              <textarea
                value={manualFormData.code}
                onChange={(e) => {
                  setManualFormData((p) => ({ ...p, code: e.target.value }));
                  setTestResult(null);
                }}
                placeholder={`def generate_indian_mobile(input={}):\n  import random\n  num = '91' + str(random.randint(7000000000, 9999999999))\n  return num\n`}
                rows={10}
                style={{ fontFamily: 'monospace', fontSize: '13px' }}
                required
              />
              <small style={{ color: 'var(--text-muted)' }}>
                Your code should define a function that takes an <code>input</code> dictionary as its first argument.
              </small>
            </div>
       </div>

            {testResult && (
              <div style={{
                marginBottom: '15px',
                padding: '12px',
                borderRadius: '6px',
                backgroundColor: testResult.success ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                border: testResult.success ? '1px solid #10b981' : '1px solid #ef4444',
                color: testResult.success ? '#10b981' : '#ef4444'
              }}>
                <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                  {testResult.success ? '✅ Verification Successful' : '❌ Verification Failed'}
                </div>
                {testResult.success ? (
                  <div style={{ fontSize: '13px' }}>
                    Output: <code style={{ color: 'var(--text-primary)', backgroundColor: 'rgba(255,255,255,0.05)', padding: '2px 4px' }}>
                    {JSON.stringify(testResult.result)}
                    </code>
                  </div>
                ) : (
                  <div style={{ fontSize: '13px', whiteSpace: 'pre-wrap' }}>{testResult.error}</div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => handleVerifyCode()} 
                disabled={verifying || savingManual}
              >
                {verifying ? 'Verifying...' : '⚙ Verify & Run'}
              </button>
              <button 
                type="submit" 
                className="btn btn-success" 
                disabled={savingManual || verifying || !testResult?.success}
                title={!testResult?.success ? "You must verify the code before saving" : ""}
              >
                {savingManual ? 'Saving...' : '💾 Save Function'}
              </button>
            </div>
          </form>
        </div>
      )}

      <h2 style={{ marginTop: '30px' }}>Generated Functions ({functions.length})</h2>

      {loading ? (
        <div className="card">
          <p>Loading custom functions...</p>
        </div>
      ) : functions.length === 0 ? (
        <div className="card">
          <p>No custom functions generated yet.</p>
        </div>
      ) : (
        <div className="responsive-grid-4" style={{ gap: '16px' }}>
          {functions.map((fn) => {
            const isExpanded = expandedId === fn.id;
            return (
            <div key={fn.id} className="card custom-function-card" style={{ display: 'flex', flexDirection: 'column' }}>
                <div className="custom-function-head">
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <h3 style={{ margin: 0, wordBreak: 'break-all', fontSize: '18px' }}>{fn.name}</h3>
                    <p className="custom-function-meta">
                      Source: {fn.generationSource || '-'} {fn.model ? `| Model: ${fn.model}` : ''} |{' '}
                      {fn.createdAt ? new Date(fn.createdAt).toLocaleString() : '-'}
                    </p>
                  </div>
                </div>

                <div style={{ flex: 1, minHeight: '140px' }}>
                  <p style={{ margin: '0 0 10px 0', fontSize: '13px', lineHeight: '1.5' }}>
                    <strong>Description:</strong> {fn.description}
                  </p>
                  <p style={{ margin: '0 0 10px 0', fontSize: '13px', lineHeight: '1.5' }}>
                    <strong>Example:</strong> {fn.example}
                  </p>
                  {fn.warning && (
                    <p style={{ margin: '0 0 10px 0', color: 'var(--warning)', fontSize: '13px' }}>
                      <strong>Warning:</strong> {fn.warning}
                    </p>
                  )}

                  {isExpanded && (
                    <div style={{ marginTop: '10px' }}>
                      <label style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', display: 'block' }}>
                        Edit Function Code:
                      </label>
                      <textarea
                        className="custom-function-code"
                        value={editingCode}
                        onChange={(e) => {
                          setEditingCode(e.target.value);
                          // Clear previous test result when code changes
                          if (verificationResults[fn.id]) {
                            setVerificationResults(prev => {
                              const next = { ...prev };
                              delete next[fn.id];
                              return next;
                            });
                          }
                        }}
                        style={{ 
                          width: '100%', 
                          height: '240px', 
                          fontFamily: 'monospace',
                          fontSize: '12px',
                          padding: '10px',
                          borderRadius: '8px',
                          border: '1px solid var(--border)',
                          backgroundColor: 'var(--bg-input)',
                          color: 'var(--text-main)',
                          resize: 'vertical'
                        }}
                      />
                      
                      {verificationResults[fn.id] && (
                        <div style={{
                          marginTop: '12px',
                          padding: '12px',
                          borderRadius: '8px',
                          backgroundColor: verificationResults[fn.id].success ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                          border: `1px solid ${verificationResults[fn.id].success ? 'var(--success)' : 'var(--danger)'}`,
                          fontSize: '13px'
                        }}>
                          <div style={{ fontWeight: 'bold', color: verificationResults[fn.id].success ? 'var(--success)' : 'var(--danger)', marginBottom: '4px' }}>
                            {verificationResults[fn.id].success ? '✅ Success' : '❌ Error'}
                          </div>
                          {verificationResults[fn.id].success ? (
                             <div>Result: <code style={{ backgroundColor: 'rgba(0,0,0,0.2)', padding: '2px 6px', borderRadius: '4px' }}>{JSON.stringify(verificationResults[fn.id].result)}</code></div>
                          ) : (
                             <div style={{ whiteSpace: 'pre-wrap', fontSize: '12px', marginTop: '4px' }}>{verificationResults[fn.id].error}</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="action-buttons" style={{ gap: '8px', marginTop: '15px' }}>
                  {isExpanded ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => handleVerifyCode({ code: editingCode, fnId: fn.id, fnName: fn.name })}
                        disabled={verificationResults[fn.id]?.verifying || savingId === fn.id}
                        style={{ flex: 1 }}
                      >
                        {verificationResults[fn.id]?.verifying ? 'Verifying...' : '⚙ Verify & Run'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={() => handleSave(fn.id)}
                        disabled={savingId === fn.id || !verificationResults[fn.id]?.success}
                        title={!verificationResults[fn.id]?.success ? "Please verify the function runs successfully before saving updates." : ""}
                        style={{ flex: 1 }}
                      >
                        {savingId === fn.id ? 'Saving...' : 'Save Updates'}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => toggleExpansion(fn)}
                      style={{ flex: 1 }}
                    >
                      View/Update Code
                    </button>
                  )}
                  {isExpanded && (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => toggleExpansion(fn)}
                      style={{ flex: 1 }}
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => handleDelete(fn.id)}
                    disabled={deletingId === fn.id || savingId === fn.id}
                    style={{ flex: isExpanded ? 0.5 : 1 }}
                  >
                    {deletingId === fn.id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {confirmDeleteData && (
        <div className="modal-overlay" onClick={() => setConfirmDeleteData(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2 style={{ color: 'var(--danger)', margin: 0 }}>Warning: Function in Use</h2>
              <button 
                className="modal-close" 
                onClick={() => {
                  setConfirmDeleteData(null);
                  setHardDeleteConfirmText('');
                }}
              >
                &times;
              </button>
            </div>

            <div className="modal-body">
              <p>
                The custom function <strong>"{confirmDeleteData.name}"</strong> is currently used in the following test suites:
              </p>
              <div style={{ 
                backgroundColor: 'rgba(255, 68, 68, 0.1)', 
                padding: '12px', 
                borderRadius: '8px', 
                marginBottom: '15px',
                border: '1px solid var(--danger)'
              }}>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {confirmDeleteData.suites.map((suite, i) => (
                    <li key={i}><strong>{suite}</strong></li>
                  ))}
                </ul>
              </div>
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '15px' }}>
                It is highly recommended that you remove this function from these suites before deleting it.
              </p>
              
              <div style={{ padding: '15px', backgroundColor: 'var(--bg-input)', borderRadius: '6px', border: '1px solid var(--border)' }}>
                <h3 style={{ marginTop: 0, fontSize: '16px' }}>Hard Delete?</h3>
                <p style={{ fontSize: '13px', marginBottom: '10px' }}>
                  To perform a hard delete, please type <strong>I UNDERSTAND</strong> below:
                </p>
                <input 
                  type="text" 
                  placeholder='Type "I UNDERSTAND"'
                  value={hardDeleteConfirmText}
                  onChange={(e) => setHardDeleteConfirmText(e.target.value)}
                  style={{ width: '100%', marginBottom: 0 }}
                />
              </div>
            </div>
            
            <div className="modal-actions">
              <button 
                className="btn btn-danger" 
                style={{ flex: 1 }}
                disabled={hardDeleteConfirmText !== 'I UNDERSTAND' || deletingId}
                onClick={() => handleDelete(confirmDeleteData.id, true)}
              >
                {deletingId ? 'Deleting...' : 'Proceed with Hard Delete'}
              </button>
              <button 
                className="btn btn-secondary" 
                style={{ flex: 1 }}
                onClick={() => {
                  setConfirmDeleteData(null);
                  setHardDeleteConfirmText('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {modalAlert && (
        <div className="modal-overlay" onClick={() => setModalAlert(null)}>
          <div className="modal-content modal-content-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ color: modalAlert.type === 'error' ? 'var(--danger)' : 'var(--success)', margin: 0 }}>
                {modalAlert.title}
              </h2>
              <button className="modal-close" onClick={() => setModalAlert(null)}>x</button>
            </div>
            <div className="modal-body">
              <p style={{ margin: '10px 0', lineHeight: 1.5 }}>{modalAlert.message}</p>
            </div>
            <div className="modal-actions">
              <button 
                className="btn btn-primary" 
                onClick={() => setModalAlert(null)}
                style={{ 
                  backgroundColor: modalAlert.type === 'error' ? 'var(--danger)' : 'var(--primary)', 
                  borderColor: modalAlert.type === 'error' ? 'var(--danger)' : 'var(--primary)',
                  width: '100%',
                  padding: '10px'
                }}
              >
                Understood
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomFunctions;
